// App-wide timezone configuration: Eastern Time (Canada)
// 'America/Toronto' automatically handles EST/EDT transitions
export const APP_TIMEZONE = 'America/Toronto';

export const formatDateTime = (date, options = {}) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-CA', { timeZone: APP_TIMEZONE, ...options });
};

export const formatDate = (date, options = {}) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: APP_TIMEZONE, ...options });
};

export const formatTime = (date, options = {}) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-CA', { timeZone: APP_TIMEZONE, ...options });
};

// Get current date/time in Eastern Time as a formatted string
export const nowET = () => {
  return new Date().toLocaleString('en-CA', { timeZone: APP_TIMEZONE });
};

// Get a Date-like object with Eastern Time values for form inputs (datetime-local)
// Returns an ISO-like string suitable for <input type="datetime-local">
export const toETInputValue = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(d);
  const get = (type) => parts.find(p => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

// Get just the date portion for <input type="date">
export const toETDateValue = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return formatDate(d, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

// Convert a datetime-local input value (interpreted as ET) to a UTC ISO string
// e.g., "2026-03-24T07:00" (meaning 7 AM ET) → "2026-03-24T11:00:00.000Z" (UTC)
export const etInputToUTC = (dateTimeLocalStr) => {
  if (!dateTimeLocalStr) return '';
  const cleanStr = dateTimeLocalStr.replace('Z', '');
  // Treat input as if it were UTC to find the ET offset for that moment
  const asUTC = new Date(cleanStr + 'Z');
  if (isNaN(asUTC.getTime())) return '';
  // Find what ET clock shows for this UTC moment
  const etParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  }).formatToParts(asUTC);
  const get = (type) => etParts.find(p => p.type === type)?.value || '00';
  const h = get('hour') === '24' ? '00' : get('hour');
  const etAsUTC = new Date(`${get('year')}-${get('month')}-${get('day')}T${h}:${get('minute')}:${get('second')}Z`);
  // Offset = UTC time - ET display = how far behind ET is
  const offsetMs = asUTC.getTime() - etAsUTC.getTime();
  // User typed ET time, actual UTC = ET + offset
  return new Date(asUTC.getTime() + offsetMs).toISOString();
};

// Get today's date string (YYYY-MM-DD) in Eastern Time
export const todayET = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date());
  const get = (type) => parts.find(p => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};
